import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PHOTON_MODES,
  getPhotonLang,
  useCityAutocomplete,
} from "../../../app/hooks/useCityAutocomplete";
import { getCountryName } from "../../../common/countries/countries";

export const ADDRESS_FIELDS = {
  CITY: "city",
  POSTCODE: "postcode",
};

// Photon renvoie l'emprise en [minLon, maxLat, maxLon, minLat] ; le paramètre
// bbox attend "minLon,minLat,maxLon,maxLat".
const toBbox = (extent) =>
  Array.isArray(extent) && extent.length === 4
    ? `${extent[0]},${extent[3]},${extent[2]},${extent[1]}`
    : undefined;

// Deux résultats distincts peuvent porter le même nom de ville ou le même code
// postal (« Paris » x5, « 92110 » dans 3 pays) : la clé combine tout ce qui les
// distingue, coordonnées comprises. Les ids OSM sont absents de certains
// objets « code postal ».
const buildKey = (item) =>
  [item.city, item.postcode, item.countryCode, item.lat.toFixed(4), item.lng.toFixed(4)].join("|");

/**
 * Autocomplétion d'adresse, limitée au pays choisi (countryCode requis : sans
 * lui aucune requête, un code postal seul étant ambigu entre pays). Le champ
 * actif détermine le type de recherche — ville (lieux habités) ou code postal
 * — plutôt qu'une heuristique sur le texte saisi.
 *
 * Une fois une ville sélectionnée, la recherche de code postal est restreinte
 * à son emprise : c'est ainsi que l'utilisateur découvre les codes d'une ville
 * qui en compte plusieurs (Paris : 75001…75020), sans requête d'énumération
 * supplémentaire — l'API publique de Komoot limite l'usage par IP.
 *
 * Les suggestions sont exactement les résultats de l'API (aucun texte tapé
 * n'est ajouté à la liste) et portent une clé unique (buildKey). Le champ
 * actif est exposé pour n'afficher la liste que sous celui-ci.
 */
export const useAddressAutocomplete = ({ countryCode }) => {
  const { i18n } = useTranslation();
  const [field, setField] = useState(ADDRESS_FIELDS.CITY);
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState(null);

  const isPostcodeSearch = field === ADDRESS_FIELDS.POSTCODE;
  const { results, loading, hasRequested, unavailable } = useCityAutocomplete(query, {
    mode: isPostcodeSearch ? PHOTON_MODES.POSTCODE : PHOTON_MODES.PLACE,
    countryCode,
    bbox: isPostcodeSearch ? toBbox(place?.extent) : undefined,
    // Les noms de ville suivent la langue de l'interface (fr, en, de ; sinon
    // noms locaux) ; le pays, lui, est traduit côté client par Intl.
    lang: getPhotonLang(i18n.language),
  });

  const richResults = useMemo(() => {
    const enriched = results.map((item) => ({
      ...item,
      key: buildKey(item),
      label: [item.city, item.postcode, getCountryName(item.countryCode, i18n.language) || item.country]
        .filter(Boolean)
        .join(", "),
    }));

    if (!isPostcodeSearch) return enriched;
    // Codes de la ville sélectionnée d'abord (l'emprise peut déborder sur des
    // communes voisines), puis ordre croissant.
    return [...enriched].sort((a, b) => {
      const aOwn = place?.name && a.city === place.name ? 0 : 1;
      const bOwn = place?.name && b.city === place.name ? 0 : 1;
      return aOwn - bOwn || a.postcode.localeCompare(b.postcode, undefined, { numeric: true });
    });
  }, [results, i18n.language, isPostcodeSearch, place]);

  const options = useMemo(
    () => richResults.map((item) => ({ key: item.key, label: item.label })),
    [richResults]
  );

  // Saisie dans l'un des deux champs : c'est lui qui pilote la recherche.
  const search = (text, targetField) => {
    setField(targetField);
    setQuery(text || "");
    // Ville retapée à la main : l'emprise de l'ancienne ville ne vaut plus.
    if (targetField === ADDRESS_FIELDS.CITY) setPlace(null);
  };

  const resetPlace = () => {
    setPlace(null);
    setQuery("");
  };

  // Renvoie le patch d'adresse à appliquer, ou null pour un texte libre (rien à
  // propager au-delà du champ édité). Le pays n'en fait pas partie : les
  // résultats sont déjà limités au pays choisi.
  const resolveSelection = (key) => {
    const match = richResults.find((item) => item.key === key);
    setQuery("");
    if (!match) return null;

    if (match.isPostcode) {
      setPlace(null);
    } else {
      setPlace({ name: match.city, extent: match.extent });
    }
    return { zipPostal: match.postcode, city: match.city };
  };

  return {
    activeField: field,
    isLoading: loading,
    hasRequested,
    unavailable,
    place,
    options,
    search,
    resetPlace,
    resolveSelection,
  };
};
